import React, { useEffect, useMemo, useState } from "react";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";

const createSchema = {
  name: [required("Role name is required.")],
};

/**
 * System create: name + allow.
 * Tenant create: name + permissions (create name, then update permissions by roleId).
 * Edit (both): permissions/allow only; name is read-only.
 * Permissions must be selected from the provided list only (no free-text).
 */
const RoleFormDialog = ({
  open,
  mode = "create",
  role = null,
  allowSuggestions = [],
  permissionsLoading = false,
  loading = false,
  tenantMode = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const [formError, setFormError] = useState("");
  const [allow, setAllow] = useState([]);

  const { values, getFieldProps, validateAll, reset, isValid } =
    useFormValidation({ name: "" }, createSchema);

  const catalog = useMemo(
    () => new Set((allowSuggestions || []).map(String).filter(Boolean)),
    [allowSuggestions]
  );

  const options = useMemo(() => Array.from(catalog).sort(), [catalog]);

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ name: role?.name || "" });
  }, [open, reset, role]);

  // Keep only permissions that exist in the dropdown catalog.
  useEffect(() => {
    if (!open) return;
    const assigned = Array.isArray(role?.allow)
      ? role.allow.map(String).filter(Boolean)
      : [];

    if (permissionsLoading) return;
    setAllow(assigned.filter((name) => catalog.has(name)));
  }, [open, role, permissionsLoading, catalog]);

  const canSubmit = Boolean(
    !loading &&
      !permissionsLoading &&
      (isCreate
        ? isValid && values.name?.trim() && allow.length > 0
        : allow.length > 0)
  );

  const handleSubmit = async () => {
    if (isCreate) {
      const { isValid: ok, errors } = validateAll();
      if (!ok) {
        setFormError(errors.name || "Please correct the highlighted fields.");
        return;
      }
    }

    if (!allow.length) {
      setFormError("Add at least one permission.");
      return;
    }

    const invalid = allow.filter((name) => !catalog.has(String(name)));
    if (invalid.length) {
      setFormError(
        `Remove invalid permissions: ${invalid.join(
          ", "
        )}. Select from the permission list only.`
      );
      return;
    }

    setFormError("");
    const permissions = allow.filter(Boolean).map(String);
    try {
      if (isCreate) {
        if (tenantMode) {
          await onSubmit({
            name: values.name.trim(),
            permissions,
          });
        } else {
          await onSubmit({
            name: values.name.trim(),
            allow: permissions,
          });
        }
      } else {
        await onSubmit(
          tenantMode ? { permissions } : { allow: permissions }
        );
      }
    } catch (err) {
      setFormError(err?.message || "Unable to save role.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add Role" : "Edit Role"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      disableSubmit={!canSubmit}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create Role" : "Save Changes"}
      loadingLabel="Saving..."
    >
      <FormField
        label="Role Name"
        required={isCreate}
        disabled={!isCreate || loading}
        {...getFieldProps("name")}
        value={isCreate ? values.name : role?.name || values.name || ""}
      />
      <ChipAutocomplete
        label="Permissions"
        required
        freeSolo={false}
        options={options}
        value={allow}
        onChange={(next) => {
          const values = (Array.isArray(next) ? next : [])
            .map(String)
            .filter(Boolean);
          setAllow(values.filter((name) => catalog.has(name)));
        }}
        loading={permissionsLoading}
        disabled={loading}
        helperText={
          tenantMode
            ? "Select permissions from the current tenant permission list."
            : "Select permissions from the available list only."
        }
      />
    </FormDialog>
  );
};

export default RoleFormDialog;
